'use client';

/**
 * useNotificationRealtime Hook
 *
 * Subscribes to Supabase Realtime for new notifications.
 * When a notification is inserted, it updates the Redux store.
 */

import { useEffect, useRef, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { refreshSessionBeforeRetry } from '@/lib/supabase/realtimeGuard';
import {
  notificationRealtimeRetryDelay,
  prepareNotificationRealtimeAuth,
} from '@/lib/supabase/notificationRealtime';
import { useAppDispatch } from '@/store/hooks';
import { addNotification } from '@/store/slices/notificationsSlice';
import type { Notification, NotificationCategory } from '@/store/slices/notificationsTypes';
import logger from '@/lib/logger';

interface UseNotificationRealtimeOptions {
  /** User ID to subscribe for */
  userId: string | null;
  /** Enable subscription */
  enabled?: boolean;
  /** Callback when new notification arrives */
  onNotification?: (notification: Notification) => void;
}

export function useNotificationRealtime(options: UseNotificationRealtimeOptions) {
  const { userId, enabled = true, onNotification } = options;
  const dispatch = useAppDispatch();
  const channelRef = useRef<ReturnType<ReturnType<typeof createClient>['channel']> | null>(null);
  const retryCountRef = useRef(0);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [retrySeed, setRetrySeed] = useState(0);

  useEffect(() => {
    if (!userId || !enabled) {
      setIsSubscribed(false);
      return;
    }

    const supabase = createClient();
    let cancelled = false;
    let retryTimer: number | null = null;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const removeCurrentChannel = () => {
      if (!channel) return;
      supabase.removeChannel(channel);
      if (channelRef.current === channel) channelRef.current = null;
      channel = null;
      if (typeof supabase.getChannels === 'function' && supabase.getChannels().length === 0) {
        supabase.realtime.disconnect();
      }
    };

    const subscribe = async () => {
      // RLS uses the JWT installed when the socket subscribes. Finish auth
      // first; the previous fire-and-forget call occasionally subscribed as anon.
      try {
        await prepareNotificationRealtimeAuth(supabase);
        if (cancelled) return;
      } catch (error) {
        logger.warn('NotificationRealtime', 'Unable to prepare Realtime auth; retrying subscription', {
          error: error instanceof Error ? error.message : String(error),
        });
      }

      if (cancelled) return;

      // Create the channel only after the current auth token is installed.
      channel = supabase
        .channel(`notifications:${userId}`)
        .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          logger.debug('NotificationRealtime', 'New notification received', { id: (payload.new as { id?: string })?.id });

          // Transform to our format
          const notification = transformNotification(payload.new);

          // Add to Redux store (this also updates unread counts internally)
          dispatch(addNotification(notification));

          // Call callback if provided (e.g., for playing notification sounds)
          if (onNotification) {
            onNotification(notification);
          }
        }
        )
        .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          logger.debug('NotificationRealtime', 'Notification updated', { id: (payload.new as { id?: string })?.id });
          // Note: We handle updates optimistically in the hooks,
          // so we don't need to update the store here unless
          // we want to sync updates from other devices
        }
        )
        .subscribe((status, err) => {
        if (cancelled) return;
        // Supabase's .subscribe() callback receives (status, err). Capturing
        // err is essential — without it, CHANNEL_ERROR is opaque and the only
        // visible signal is endless backoff with no root cause.
        logger.debug('NotificationRealtime', 'Subscription status changed', {
          status,
          err: err ? { message: err.message, name: err.name } : undefined,
        });
        if (status === 'SUBSCRIBED') {
          retryCountRef.current = 0;
          setIsSubscribed(true);
          return;
        }
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          setIsSubscribed(false);
          removeCurrentChannel();
          void refreshSessionBeforeRetry('NotificationRealtime').then(() => {
            if (cancelled) return;
            retryCountRef.current += 1;
            const retryInMs = notificationRealtimeRetryDelay(retryCountRef.current);
            logger.warn('NotificationRealtime', 'Realtime channel error, backing off', {
              status,
              errMessage: err?.message,
              errName: err?.name,
              retryInMs,
            });
            retryTimer = window.setTimeout(() => {
              if (!cancelled) setRetrySeed((prev) => prev + 1);
            }, retryInMs);
          });
          return;
        }
        if (status === 'CLOSED') {
          setIsSubscribed(false);
        }
        });

      channelRef.current = channel;
    };

    setIsSubscribed(false);
    void subscribe();

    // Cleanup on unmount
    return () => {
      cancelled = true;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      removeCurrentChannel();
      setIsSubscribed(false);
    };
  }, [userId, enabled, dispatch, onNotification, retrySeed]);

  return {
    /** Whether the subscription is active */
    isSubscribed,
  };
}

/**
 * Transform database row to Notification type
 */
function transformNotification(row: any): Notification {
  return {
    id: row.id,
    userId: row.user_id,
    orgUnitId: row.org_unit_id,
    category: row.category as NotificationCategory,
    priority: row.priority,
    behavior: row.behavior || 'actionable',
    title: row.title,
    body: row.body,
    icon: row.icon,
    actionUrl: row.action_url,
    actionLabel: row.action_label,
    metadata: row.metadata || {},
    isRead: row.is_read ?? false,
    readAt: row.read_at,
    isArchived: row.is_archived ?? false,
    deliveredInapp: row.delivered_inapp ?? false,
    deliveredPush: row.delivered_push ?? false,
    deliveredEmail: row.delivered_email ?? false,
    createdAt: row.created_at,
    expiresAt: row.expires_at,
  };
}

export default useNotificationRealtime;
